import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [form, setForm] = useState({
    name: "",
    status: "planlagt",
    phase: "design",
    budget: "",
  });

  const handleChange = (e) => {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  async function loadProjects() {
    setErrorMsg("");
    setLoading(true);

    try {
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError) throw userError;
      if (!user) throw new Error("Not logged in");

      const { data, error } = await supabase
        .from("projects")
        .select("id, name, status, phase, budget, expenses, created_at")
        .order("created_at", { ascending: false });

      if (error) throw error;

      setProjects(data || []);
    } catch (err) {
      console.error("loadProjects error:", err);
      setProjects([]);
      setErrorMsg(err?.message || "Kunne ikke hente projekter.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    const name = form.name.trim();
    if (!name) return;

    setSaving(true);

    try {
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError) throw userError;
      if (!user) throw new Error("Not logged in");

      const budgetVal =
        form.budget === "" ? null : Number(String(form.budget).replace(",", "."));

      const payload = {
        user_id: user.id,
        name,
        status: form.status,      // skal matche dine check constraints
        phase: form.phase,        // skal matche dine check constraints
        budget: budgetVal,
        expenses: 0,
      };

      const { error } = await supabase.from("projects").insert([payload]);
      if (error) throw error;

      // Mønster A: reload fra DB
      setForm({ name: "", status: "planlagt", phase: "design", budget: "" });
      await loadProjects();
    } catch (err) {
      console.error("create project error:", err);
      setErrorMsg(err?.message || "Kunne ikke oprette projekt.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-sm">
      <h1 className="text-2xl font-bold mb-6 text-gray-800">Projekter</h1>

      {errorMsg ? (
        <div className="mb-4 p-3 rounded bg-red-50 text-red-700 text-sm border border-red-200">
          {errorMsg}
        </div>
      ) : null}

      {/* Formular til nyt projekt (DB) */}
      <form
        onSubmit={handleSubmit}
        className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8"
      >
        <input
          type="text"
          name="name"
          placeholder="Projektnavn"
          value={form.name}
          onChange={handleChange}
          className="border p-2 rounded md:col-span-2"
          required
        />

        <select
  name="status"
  value={form.status}
  onChange={handleChange}
  className="border p-2 rounded"
>
  <option value="planlagt">Planlagt</option>
  <option value="igangværende">I gang</option>
  <option value="afsluttet">Afsluttet</option>
</select>

        <select
          name="phase"
          value={form.phase}
          onChange={handleChange}
          className="border p-2 rounded"
        >
          <option value="design">Design</option>
          <option value="byggeri">Byggeri</option>
          <option value="afslutning">Afslutning</option>
        </select>

        <input
          type="number"
          name="budget"
          placeholder="Budget (kr.)"
          value={form.budget}
          onChange={handleChange}
          className="border p-2 rounded md:col-span-2"
        />

        <button
          type="submit"
          disabled={saving}
          className="md:col-span-2 bg-blue-600 text-white py-2 rounded hover:bg-blue-700 disabled:opacity-60"
        >
          {saving ? "Opretter..." : "Opret projekt"}
        </button>
      </form>

      {/* Projektliste */}
      {loading ? (
        <p className="text-gray-500">Henter projekter...</p>
      ) : projects.length === 0 ? (
        <p className="text-gray-500">Ingen projekter oprettet endnu.</p>
      ) : (
        <table className="w-full border text-left">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-2 border">Projektnavn</th>
              <th className="p-2 border">Status</th>
              <th className="p-2 border">Fase</th>
              <th className="p-2 border">Budget</th>
              <th className="p-2 border">Udgifter</th>
              <th className="p-2 border">Oprettet</th>
              <th className="p-2 border"></th>
            </tr>
          </thead>
          <tbody>
            {projects.map((p) => (
              <tr key={p.id}>
                <td className="p-2 border">{p.name}</td>
                <td className="p-2 border capitalize">{p.status}</td>
                <td className="p-2 border capitalize">{p.phase}</td>
                <td className="p-2 border">{(p.budget ?? 0).toLocaleString()} kr.</td>
                <td className="p-2 border">{(p.expenses ?? 0).toLocaleString()} kr.</td>
                <td className="p-2 border text-sm text-gray-500">
                  {p.created_at ? new Date(p.created_at).toLocaleString() : ""}
                </td>
                <td className="p-2 border">
                  <Link className="text-blue-600 hover:underline" to={`/projects/${p.id}`}>
                    Åbn
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
